import React from 'react';
import { useMediaQuery } from 'react-responsive';

import { isTabletOrMobileQuery } from '~/react-utils';
import { formatLinkProperties } from '~/utils/formatLinkProperties';

import { Picture } from '../ui/Picture';

import begambleaware from './img/compliance/begambleaware.svg';
import gamblingtherapy from './img/compliance/gambling-therapy.svg';
import gamstop from './img/compliance/gamstop.svg';
import raigSvg from './img/compliance/raig.svg';
import logo from './img/oddschecker.svg';
import facebook from './img/social/facebook.svg';
import instagram from './img/social/instagram.svg';
import snapchat from './img/social/snapchat.svg';
import twitter from './img/social/twitter.svg';
import youtube from './img/social/youtube.svg';
import styles from './FooterOC.module.css';

const standardLinks = [
{
  title: 'About Us',
  href: 'https://support.oddschecker.com/hc/en-us'
},
{
  title: 'Advertising',
  href: 'mailto:marketing@oddschecker.com'
},
{
  title: 'Contact Us',
  href: 'https://support.oddschecker.com/hc/en-us/requests/new'
},
{
  title: 'Help',
  href: 'https://support.oddschecker.com/hc/en-us'
},
{
  title: 'Careers',
  href: 'https://careers.oddschecker.com/'
},
{
  title: 'Terms and Conditions',
  href: 'myoddschecker/terms-and-conditions'
},
{
  title: 'Privacy & Cookies',
  href: 'myoddschecker/cookies'
},
{
  title: 'Mobile',
  href: 'https://www.oddschecker.com/app'
}];


export const FooterOC = (): JSX.Element => {
  const isTabletOrMobile = useMediaQuery(isTabletOrMobileQuery);

  const links = [...standardLinks];

  if (isTabletOrMobile) {
    links.splice(4, 0, {
      title: 'Casino',
      href: 'casino'
    });
  }

  const socialIconsSize = {
    height: isTabletOrMobile ? 30 : 40,
    width: isTabletOrMobile ? 30 : 40
  };

  return (
    <section className={styles.footerContainer} data-testid="footer-oc">
			<div className={`container ${styles.footerWrapper}`}>
				<div className={styles.footerCenter}>
					<a
            data-testid="oc-logo-link"
            {...isTabletOrMobile ?
            formatLinkProperties('https://www.oddschecker.com/') :
            {}}>

						<Picture
              altText="oddschecker logo"
              className={styles.footerLogoLink}
              svgSrc={logo} />

					</a>
					<div className={styles.footerLinksWrapper}>
						<div
              className={styles.socialLogosWrapper}
              data-testid="social-logos">

							<a
                href="https://www.facebook.com/OddscheckerBetting"
                rel="noopener noreferrer"
                target="_blank"
                title="Facebook">

								<Picture
                  altText="facebook icon"
                  className={styles.socialIcon}
                  height={socialIconsSize.height}
                  svgSrc={facebook}
                  width={socialIconsSize.width} />

							</a>
							<a
                href="https://www.instagram.com/oddscheckertv"
                rel="noopener noreferrer"
                target="_blank"
                title="Instagram">

								<Picture
                  altText="instagram icon"
                  className={styles.socialIcon}
                  height={socialIconsSize.height}
                  svgSrc={instagram}
                  width={socialIconsSize.width} />

							</a>
							<a
                href="https://www.snapchat.com/add/OddscheckerUK"
                rel="noopener noreferrer"
                target="_blank"
                title="Snapchat">

								<Picture
                  altText="snapchat icon"
                  className={styles.socialIcon}
                  height={socialIconsSize.height}
                  svgSrc={snapchat}
                  width={socialIconsSize.width} />

							</a>
							<a
                href="https://www.youtube.com/user/Oddschecker"
                rel="noopener noreferrer"
                target="_blank"
                title="Youtube">

								<Picture
                  altText="youtube icon"
                  className={styles.socialIcon}
                  height={socialIconsSize.height}
                  svgSrc={youtube}
                  width={socialIconsSize.width} />

							</a>
							<a
                href="https://twitter.com/oddschecker"
                rel="noopener noreferrer"
                target="_blank"
                title="Twitter">

								<Picture
                  altText="twitter icon"
                  className={styles.socialIcon}
                  height={socialIconsSize.height}
                  svgSrc={twitter}
                  width={socialIconsSize.width} />

							</a>
						</div>
						<div
              className={styles.standardLinksContainer}
              data-testid="general-links">

							<ul className={styles.standardLinksList}>
								{links.map(({ href, title }, idx) =>
                <li
                  className={styles.standardLinksListItem}
                  key={`${title.toLowerCase()}_${idx}`}>

										{idx > 0 && <span className={styles.separatorDot}>·</span>}
										<a
                    className={styles.standardLink}
                    data-testid="general-link"
                    {...formatLinkProperties(href)}>

											{title}
										</a>
									</li>
                )}
							</ul>
						</div>
					</div>
				</div>
				<div className={styles.logosWrapper} data-testid="logo-links">
					<a
            href="https://www.gamstop.co.uk/"
            rel="noopener noreferrer"
            target="_blank"
            title="GamStop">

						<Picture
              altText="gamstop icon"
              className={styles.gamStop}
              height={13}
              svgSrc={gamstop}
              width={54} />

					</a>
					<a
            href="https://www.begambleaware.org/"
            rel="noopener noreferrer"
            target="_blank"
            title="BeGambleAware">

						<Picture
              altText="gamble aware icon"
              className={styles.gambleAware}
              height={22}
              svgSrc={begambleaware}
              width={116} />

					</a>
					<a
            href="https://www.raig.org/"
            rel="noopener noreferrer"
            target="_blank"
            title="Raig">

						<Picture
              altText="raig icon"
              className={styles.raig}
              height={18}
              svgSrc={raigSvg}
              width={55} />

					</a>
					<a
            href="https://www.gamblingtherapy.org/en"
            rel="noopener noreferrer"
            target="_blank"
            title="Gambling Therapy">

						<Picture
              altText="gambling therapy icon"
              className={styles.gamblingTherapy}
              height={22}
              svgSrc={gamblingtherapy}
              width={60} />

					</a>
				</div>
			</div>
		</section>);

};